import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import {
    Badge,
    Heading,
    LoadingProgress,
    Panel,
    Table,
    TableFooter,
    Text,
} from "@/ui";
import useMeta from "../../base/hooks/useMeta.js";
import { formatAddress, updateAccountAttribute } from "../../utils/utils";
import { QUERY_STORES_FROM_ACCOUNT_LIST } from "../../apollo/module-operations/store";
import { RowActionsMenu, storeTableUiStyles } from "../store/shared";
import styles from "../store/GeneralsForm.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const AccountStores = ({ toolbar }) => {
    const { resourceId, isCreate } = useMeta();
    const history = useHistory();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const {
        data: stores,
        loading: storeLoading,
        networkStatus,
    } = useQuery(QUERY_STORES_FROM_ACCOUNT_LIST, {
        variables: {
            account_id: resourceId,
        },
        skip: isCreate,
    });

    const storeRows = useMemo(
        () => (stores && stores.listStoresFromAccount) || [],
        [stores],
    );

    const totalPages = Math.max(1, Math.ceil(storeRows.length / pageSize) || 1);
    const currentPage = Math.min(page, totalPages);
    const pageStart = (currentPage - 1) * pageSize;
    const pageRows = useMemo(
        () => storeRows.slice(pageStart, pageStart + pageSize),
        [storeRows, pageStart, pageSize],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            label={row.name || row.id || "store"}
                            onUpdate={() => {
                                updateAccountAttribute(
                                    resourceId,
                                    row.id,
                                    history,
                                );
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 140,
                renderCell: row => row.name || "",
            },
            {
                id: "email",
                header: "Email",
                minWidth: 180,
                renderCell: row => row.email || "",
            },
            {
                id: "phone",
                header: "Phone",
                minWidth: 120,
                renderCell: row => row.phone_number || "",
            },
            {
                id: "address",
                header: "Address",
                minWidth: 220,
                renderCell: row =>
                    formatAddress({
                        address1: row.address1,
                        address2: row.address2,
                        city: row.city,
                        country: row.country,
                        country_code: row.country_code,
                        country_name: row.country_name,
                        state: row.state,
                        state_code: row.state_code,
                    }),
            },
            {
                id: "status",
                header: "Status",
                minWidth: 110,
                renderCell: row => (
                    <Badge variant={row.status ? "primary" : "neutral"}>
                        {row.status ? "Active" : "No Active"}
                    </Badge>
                ),
            },
        ],
        [resourceId, history],
    );

    const _storeLoading = storeLoading || networkStatus === 4;

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Stores</Heading>
                        <Text tone="secondary" variant="body-md">
                            All Business Stores
                        </Text>
                    </div>
                    {toolbar}
                </div>
            }
            showHeaderDivider
        >
            {_storeLoading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        getRowId={row => String(row.id)}
                        rows={pageRows}
                    />
                    <TableFooter
                        onPageChange={setPage}
                        onPageSizeChange={size => {
                            setPageSize(size);
                            setPage(1);
                        }}
                        page={currentPage}
                        pageSize={pageSize}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        totalPages={totalPages}
                        totalRows={storeRows.length}
                    />
                </>
            )}
        </Panel>
    );
};

export default AccountStores;
